// src/app/core/firebase/firebase.module.ts
import { APP_INITIALIZER, NgModule } from '@angular/core'; // Removido Injector, pois não será mais usado diretamente
import { CommonModule } from '@angular/common';

// --- Imports do AngularFire ---
import { initializeApp, provideFirebaseApp, getApp } from '@angular/fire/app';
// Importe as *classes/interfaces* para usar como tokens nos 'deps'
import { Auth, provideAuth, connectAuthEmulator } from '@angular/fire/auth';
import { Firestore, getFirestore, provideFirestore, connectFirestoreEmulator } from '@angular/fire/firestore';

// IMPORTS CORRETOS DO SDK MODULAR (OBRIGATÓRIO)
// initializeAuth (controle explícito da persistência)
import {
  browserPopupRedirectResolver,
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';

import { Storage, getStorage, provideStorage, connectStorageEmulator } from '@angular/fire/storage'; // <-- Use 'Storage' aqui, que é o token para o serviço de Storage
import { Functions, getFunctions, provideFunctions, connectFunctionsEmulator } from '@angular/fire/functions';
// ------------------------------

import { environment } from '@environments/environment';

const LOG_PREFIX = '[Firebase Emulator]';

@NgModule({
  declarations: [],
  imports: [
    CommonModule,
  ],
  providers: [
    // --- Provedores do AngularFire ---
    provideFirebaseApp(() => initializeApp(environment.firebase)), // Inicializa o Firebase com as credenciais do ambiente

    provideAuth(() => {
      // =====================================================
      // 🔥 FIX SAFARI iOS — INICIALIZAÇÃO CONTROLADA
      // -----------------------------------------------------
      // ⚠️ NÃO usar setPersistence()
      // ⚠️ NÃO usar browserLocalPersistence
      // ⚠️ NÃO usar getAuth() (causa auth/already-initialized)
      //
      // ✔ initializeAuth define persistence na criação
      // ✔ getApp() reutiliza a instância já criada pelo provideFirebaseApp
      // =====================================================

      const app = getApp(); // garante reutilização da instância correta

      // Safari: prioriza persistência local para preservar
      // a autenticação entre sessões do navegador.
      // Mantém sessionStorage como fallback.
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

      if (isSafari) {
        console.log(
          'Firebase Auth: Safari detected → localPersistence com fallback'
        );

        return initializeAuth(app, {
          persistence: [
            browserLocalPersistence,
            browserSessionPersistence
          ],
          popupRedirectResolver: browserPopupRedirectResolver
        });
      }

      // Outros browsers usam IndexedDB
      console.log('Firebase Auth: IndexedDB persistence');

      return initializeAuth(app, {
        persistence: indexedDBLocalPersistence,
        popupRedirectResolver: browserPopupRedirectResolver
      });
    }),

    provideFirestore(() => getFirestore()),
    provideStorage(() => getStorage()), // Fornece a instância do Firebase Storage SDK
    provideFunctions(() => getFunctions()), // Fornece a instância do Firebase Functions SDK
    // ---------------------------------

    // Usar APP_INITIALIZER para executar a lógica de conexão aos emuladores
    {
      provide: APP_INITIALIZER,
      // O Angular injeta os serviços (Auth, Firestore, Storage, Functions) DIRETAMENTE
      // na função factory 'useFactory' usando seus tokens (as classes/interfaces).
      useFactory: (
        auth: Auth, // Injeta a instância do serviço de autenticação
        firestore: Firestore, // Injeta a instância do serviço do Firestore
        storage: Storage, // Injeta a instância do serviço de Storage
        functions: Functions // Injeta a instância do serviço de Functions
      ) => () => { // A função factory retorna uma função (o initializer)
        const useEmulators =
          !!environment.firebaseAuthEmulatorHost;

        if (useEmulators) {
          console.log('--- Conectando aos Firebase Emulators ---');

          // Auth Emulator
          if (environment.firebaseAuthEmulatorHost) {
            connectAuthEmulator(auth, environment.firebaseAuthEmulatorHost, { disableWarnings: true });
            console.log(`${LOG_PREFIX} Auth conectado a:`, environment.firebaseAuthEmulatorHost);
          }

          // Firestore Emulator
          if (environment.firebaseFirestoreEmulatorHost && environment.firebaseFirestoreEmulatorPort) {
            connectFirestoreEmulator(
              firestore,
              environment.firebaseFirestoreEmulatorHost,
              environment.firebaseFirestoreEmulatorPort
            );
            console.log(
              `${LOG_PREFIX} Firestore conectado a:`,
              environment.firebaseFirestoreEmulatorHost,
              environment.firebaseFirestoreEmulatorPort
            );
          }

          // Storage Emulator
          if (environment.firebaseStorageEmulatorHost && environment.firebaseStorageEmulatorPort) {
            connectStorageEmulator(
              storage,
              environment.firebaseStorageEmulatorHost,
              environment.firebaseStorageEmulatorPort
            );
            console.log(
              `${LOG_PREFIX} Storage conectado a:`,
              environment.firebaseStorageEmulatorHost,
              environment.firebaseStorageEmulatorPort
            );
          }

          // Functions Emulator (ULTRA SAFE - Safari proof)
          if (
            !environment.production &&
            typeof environment.apiUrl === 'string'
          ) {
            try {
              const url = new URL(environment.apiUrl);

              if (url.port) {
                const functionsHost = url.hostname;
                const functionsPort = parseInt(url.port, 10);

                connectFunctionsEmulator(functions, functionsHost, functionsPort);
                console.log(
                  `${LOG_PREFIX} Functions conectado a:`,
                  `http://${functionsHost}:${functionsPort}`
                );
              }
            } catch (e) {
              console.warn('Functions Emulator: URL inválida', e);
            }
          }

          // =====================================================
          // LOG CONSOLIDADO (DEVE VIR APÓS TODAS AS CONEXÕES)
          // =====================================================
          console.log('[Firebase Emulator] RESUMO:', {
            auth: environment.firebaseAuthEmulatorHost,
            firestore: `${environment.firebaseFirestoreEmulatorHost}:${environment.firebaseFirestoreEmulatorPort}`,
            storage: `${environment.firebaseStorageEmulatorHost}:${environment.firebaseStorageEmulatorPort}`,
            functions: environment.apiUrl,
          });

          console.log('-----------------------------------------');
        }
      },
      // Declara as dependências que o Angular deve injetar na função factory
      deps: [Auth, Firestore, Storage, Functions],
      multi: true
    }
  ],
  exports: []
})
export class FirebaseModule {}
